<template>
  <div
    ref="mainCollection"
    class="main-collection"
  >
    <div class="main-collection__texts-wrapper">
      <div class="main-collection__title-wrapper">
        <h1
          :id="`section-title-${liquid.sectionId}`"
          class="main-collection__title text-body-s-bold-desktop text-body-xs-bold"
          :class="{
            [$class('hidden')]: collectionBannerHideTitle,
            'span-font': collectionBannerFont
          }"
          v-text="liquid.collectionTitle"
        />
        <span
          v-if="productsCount > 0"
          class="main-collection__total-products text-body-3xs"
        >
          / {{ itemText }}
        </span>
      </div>

      <div
        v-if="collectionBannerSubtitle"
        class="main-collection__subtitle"
        :class="{
          'span-font': collectionBannerFont,
        }"
        v-html="collectionBannerSubtitle"
      />
    </div>

    <related-links
      id="quick-links"
      class="main-collection__quick-links"
    >
      <template
        v-for="(link, index) of quicklinks"
        #[`link${index}`]
        :key="link.url"
      >
        <btn
          class="main-search__popular-search"
          :label="link.text"
          modifiers="outlined small"
          class-label="text-label-xs"
          :url="link.url"
          :show-icon="false"
        />
      </template>
    </related-links>

    <ais-instant-search
      :index-name="algolia.indexProducts"
      :routing="algolia.routing"
      :search-client="algolia.searchClient"
      :insights="true"
    >
      <ais-configure
        :filters="algolia.filters"
        :hits-per-page.camel="pageSize"
        :faceting-after-distinct.camel="true"
        :distinct="true"
        :rule-contexts.camel="[$variable('page.handle')]"
      />

      <algolia-refinement-connector @filters-applied="filtersChange" />
      <algolia-infinite-hits-connector @hits-change="updateRetrievedHits" />

      <div
        v-show="productsCount !== -1"
        class="main-collection__filter-wrapper grid"
      >
        <button
          class="col xs2 main-collection__filters"
          @click="handleOpenMobileDrawer"
        >
          <icon-filter />
          <div
            class="algolia-filter__button-mobile text-label-xs"
            v-text="$string('collection.filter')"
          />
        </button>

        <div class="col xs2 l12 main-collection__sort-input">
          <algolia-filter
            :plp-column-count="plpColumnCount"
            :page-sizes="liquid.pageSizes"
            :page-size="pageSize"
            @page-size-update="updatePageSize"
          />
        </div>
      </div>

      <ais-current-refinements v-if="isMobile">
        <template #default="{ items }">
          <div
            v-if="items.length"
            class="algolia-filters-slider"
          >
            <algolia-filters-slider
              :items="items"
            />
          </div>
        </template>
      </ais-current-refinements>

      <ais-hits :transform-items="transformItems">
        <template #default="{ items, sendEvent }">
          <!-- algolia-stats-connector component need to be inside of ais-hits -->
          <algolia-stats-connector @nb-hits-change="nbHitsChange" />
          <algolia-custom-data-connector @custom-data="populateCustomData" />

          <div
            v-if="productsCount === 0"
            class="algolia-filter__error-message text-body-xs"
            v-text="$string('collection.no_products')"
          />

          <div class="main-collection__grid grid">
            <template v-if="items.length > 0">
              <template
                v-for="(content, index) in items"
                :key="content.id"
              >
                <product-card
                  v-if="content.id"
                  :badge="generateProductBadge(content)"
                  class="main-collection__product-card"
                  :image-order="liquid.productCardImageOrder"
                  :image-use-alt="liquid.productCardUseAltImage"
                  :index="index"
                  :product="content"
                  @add-to-cart="sendEvent('conversion', content, 'Added To Cart')"
                  @product-card-clicked="sendEvent('click', content, 'Product Clicked')"
                />

                <content-banner
                  class="content-banner"
                  :content-banner="getBannerContent(content['__position'])"
                  :plp-column-count="plpColumnCount"
                />
              </template>
            </template>
          </div>
          <ais-stats
            :class-names="{
              'ais-Stats': 'product-stats',
            }"
          >
            <template #default="{ nbHits }">
              <p
                v-if="nbHits > 0"
                class="main-collection__product-count text-body-xs"
                v-text="productCountMessage(items.length, nbHits)"
              />
            </template>
          </ais-stats>

          <algolia-pagination @page-change="pageChange" />

          <div class="main-collection__description-wrapper">
            <p
              v-if="liquid.collectionDescription"
              class="main-collection__description text-body-2xs container text-body-xs-desktop"
              v-html="liquid.collectionDescription"
            />
          </div>

          <related-links
            id="related-links"
            class="main-collection__related-links"
          >
            <template #header>
              <p
                class="text-label-xs"
                v-text="$string('collection.related_categories')"
              />
            </template>

            <template
              v-for="(link, index) of relatedLinks"
              #[`link${index}`]
              :key="link.url"
            >
              <btn
                class="main-collection__related-link"
                :label="link.text"
                modifiers="outlined small"
                class-label="text-label-xs"
                :url="link.url"
                :show-icon="false"
              />
            </template>
          </related-links>
        </template>
      </ais-hits>

      <!-- Mobile filter drawer -->
      <overlay
        key="algoliaDrawerMobile"
        block-class="algolia-drawer-mobile-overlay"
        direction="right"
        namespace="algoliaDrawerMobile"
        :template="false"
        type="drawer"
      >
        <template #body>
          <algolia-drawer-mobile />
        </template>
      </overlay>
    </ais-instant-search>

    <!-- Loading placeholder - START -->
    <template v-if="productsCount === -1">
      <div class="main-collection__filter-wrapper grid">
        <button class="col xs2 main-collection__filters">
          <icon-filter />
          <div
            class="algolia-filter__button-mobile text-label-xs"
            v-text="$string('collection.filter')"
          />
        </button>

        <div class="col xs2 l12 main-collection__sort-input">
          <algolia-filter :loading="true" />
        </div>
      </div>

      <div class="main-collection__grid grid">
        <div
          v-for="(index) of pageSize"
          :key="`product-card-${liquid.sectionId}-${index}`"
        >
          <product-card
            :key="`product-card-${liquid.sectionId}-${index}`"
            class="main-collection__product-card product card"
            :index="index"
            :product="false"
          />
        </div>
      </div>
    </template>
    <!-- Loading placeholder - END -->
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main collection component.
 *
 * @param {Number} liquid.collectionId - Collection ID.
 * @param {String} liquid.collectionTitle - Collection title.
 * @param {String} liquid.collectionImage - Collection image.
 * @param {String} liquid.collectionDescription - Collection description.
 * @param {String} liquid.pageSizes - CSV options of page sizes.
 * @param {String} liquid.defaultPageSize - default page size.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Array} liquid.sortByOptions - Array of sort by options.
 * @param {Array} quicklinks - Array of quicklink objects.
 * @param {Boolean} collectionBannerType - Enable narrow collection banner type.
 * @param {Boolean} collectionBannerFont - Enable Span collection banner font style.
 * @param {Boolean} collectionBannerHideTitle - Hide collection banner title.
 * @param {String} collectionBannerSubtitle - Collection banner subtitle.
 *
 */
import { mapActions, mapState } from 'vuex'
import algoliasearch from 'algoliasearch/lite'
import { history as historyRouter } from 'instantsearch.js/es/lib/routers'
import { singleIndex as singleIndexMapping } from 'instantsearch.js/es/lib/stateMappings'
import {
  AisConfigure,
  AisCurrentRefinements,
  AisInstantSearch,
  AisHits,
  AisStats,
} from 'vue-instantsearch/vue3/es'


import AlgoliaDrawerMobile from '~async/algolia-drawer-mobile/algolia-drawer-mobile'
import AlgoliaPagination from '~async/algolia-pagination/algolia-pagination'
import AlgoliaStatsConnector from '~async/algolia/algolia-stats-connector'
import AlgoliaCustomDataConnector from '~async/algolia/algolia-custom-data-connector'
import AlgoliaRefinementConnector from '~async/algolia/algolia-refinement-connector'
import AlgoliaInfiniteHitsConnector from '~async/algolia/algolia-infinite-hits-connector'
import AlgoliaFiltersSlider from '~async/main-collection/algolia-filters-slider'

import { formatAlgoliaProduct, generateProductBadge } from '~/helpers/format-algolia'

import AlgoliaFilter from '~async/main-collection/algolia-filter'
import ProductCard from '~async/product-card/product-card'
import RelatedLinks from '~async/related-links/related-links'
import ContentBanner from '~async/content-banner/content-banner'
import IconFilter from '~icons/misc/filter.svg'

export default {
  name: 'MainCollection',

  components: {
    AisInstantSearch,
    AisConfigure,
    AisCurrentRefinements,
    AisHits,
    AisStats,
    AlgoliaStatsConnector,
    AlgoliaCustomDataConnector,
    AlgoliaRefinementConnector,
    AlgoliaInfiniteHitsConnector,
    AlgoliaFilter,
    IconFilter,
    ProductCard,
    ContentBanner,
    RelatedLinks,
    AlgoliaDrawerMobile,
    AlgoliaPagination,
    AlgoliaFiltersSlider,
  },

  props: {
    quicklinks: {
      type: Array,
      default: () => [],
    },
    relatedLinks: {
      type: Array,
      default: () => [],
    },
    liquid: {
      type: Object,
      default: () => ({
        contentBanner: {},
        collectionId: '',
        collectionTitle: '',
        collectionImage: '',
        collectionDescription: '',
        pageSizes: '',
        productCardImageOrder: '',
        productCardUseAltImage: false,
        sectionId: '',
        sortByOptions: [],
      }),
    },
    collectionBannerType: {
      type: Boolean,
      default: true,
    },
    collectionBannerFont: {
      type: Boolean,
      default: true,
    },
    collectionBannerHideTitle: {
      type: Boolean,
      default: true,
    },
    collectionBannerSubtitle: {
      type: [String, Boolean],
      default: '',
    },
  },

  data() {
    return {
      attributes: this.$variable('algolia.filterOptions'),
      products: [],
      bannersData: {},
      productContent: [],
      productsCount: -1,
      retrievedHitsCount: 0,
      isFiltered: false,
      algolia: {
        filters: '',
        indexProducts: this.$variable('algolia.indexes.products'),
        defaultFilters: this.$variable('algolia.defaultFilters'),
        searchClient: algoliasearch(
          this.$variable('algolia.credentials.appId'),
          this.$variable('algolia.credentials.apiKey'),
        ),
        routing: {
          router: historyRouter(),
          stateMapping: singleIndexMapping(this.$variable('algolia.indexes.products')),
        },
      },
      pageType: this.$variable('page.type'),
      perPage: 0,
      plpColumnCount: this.$variable('plpColumnCount'),
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns banners data for specific plp layout (plpColumnCount).
     * @returns {Object}
     */
    bannersDataForLayout() {
      const bannersData = this.bannersData[this.plpColumnCount]
      return bannersData || {}
    },

    /**
     * Calculate the number of product card places the promo banners have taken.
     * @returns {Number}
     */
    cardPlaces() {
      const gridColumnBanners = Object.values(this.bannersDataForLayout)

      if (!gridColumnBanners.length) {
        return 0
      }

      const cardPlaces = gridColumnBanners.map(banner => {
        const { column_span = 1, row_span = 1, position = -1 } = this.getBannerDataForLayout(banner, this.plpColumnCount)

        // Surface occupied by the promo banner
        const surface = column_span * row_span
        if (position + surface > this.retrievedHitsCount) {
          return 0
        }

        return surface
      }).reduce((acc, amount) => acc + amount, 0)

      return cardPlaces
    },

    /**
     * Plural or single total item label .
     * @returns {string}
     */
    itemText() {
      return `${this.productsCount} ${this.$string('collection.items', {
        pluralise: this.productsCount,
      })}`
    },

    pageSize() {
      return Number(this.perPage || this.liquid.defaultPageSize)
    },
  },

  watch: {
    isMobile: {
      handler: function () {
        if(this.isMobile) {
          this.plpColumnCount = 2
          return
        }

        this.plpColumnCount = window.localStorage?.getItem('--column-count') || 4
      },
      immediate: true,
    },
  },

  created() {
    window.cnvs.algolia.sortByOptions = this.liquid.sortByOptions
    this.setPageFilters()
  },

  mounted() {
    this.handleHiddenSale()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Set the default filters for Algolia
     * - No filter applied if we aren't on a collections page
     * - If the `currentVendor` is set, then we are viewing a Vendors URL, so
     *   a `vendor` filter is applied.
     * - Otherwise, applies a collection filter with the filter handle.
     */
    setPageFilters() {
      this.algolia.filters = `collections.handle:"${this.$variable('page.handle')}" AND ${this.algolia.defaultFilters}`
    },

    /**
     * Transform items to properly pass props to the product card component.
     * @returns {Array}
     */
    transformItems(items) {
      return items.map((item) => formatAlgoliaProduct(item))
    },

    /**
     * Calculate the maximum allowed column span.
     * - Modifies the column count to prevent broken grid layouts
     * - For desktop:
     *   - In 3 grid layout, max column span allowed is 2.
     *   - In 4 grid layout, max column span allowed is 3.
     * - For mobile, max column span allowed is 2.
     * - Desktop requires column count - 1 to ensure that
     *   there is always a product card on the same row to provide the in grid
     *   content some height.
     *
     * @param {Object} banner - Banner data.
     * @param {Number} columnCount - Grid structure column count (2, 3 or 4).
     * @returns {Number} The correct column span.
     */
    adjustColumnSpan(banner, columnCount) {
      const { column_span = 1 } = this.getBannerDataForLayout(banner, columnCount)

      const maxSpan = columnCount === 2 ? 2 : columnCount - 1

      return Math.min(column_span, maxSpan)
    },

    /**
     * Populates 'bannersData' object with banner data.
     * @param {Object} banner - Banner data.
     * @param {Number} columnCount - Grid structure column count (2, 3 or 4).
     */
    mapBannerToPosition(banner, columnCount) {
      // eslint-disable-next-line prefer-const
      let { column_span = 1, position = -1 } = this.getBannerDataForLayout(banner, columnCount)
      column_span = this.adjustColumnSpan(banner, columnCount)

      const bannerData = this.bannersData[columnCount] || {}

      const gridColumnBanners = Object.values(bannerData)

      /**
       * To get an accurate number of grid cards left to the right, we need to
       * know how much promo cards precede this one.
       */
      const cardPlaces = gridColumnBanners.map(banner => {
        const { column_span = 1, row_span = 1, position = -1 } = this.getBannerDataForLayout(banner, columnCount)

        const surface = column_span * row_span
        if (position + surface > this.retrievedHitsCount) {
          return 0
        }

        return surface
      }).reduce((acc, amount) => acc + amount, 0)

      const cardGridsToRight = columnCount - ((position + cardPlaces) % columnCount)

      /**
       * Check if there is enough space for banner and change position
       * accordingly if needed
       */
      const adjustedPosition = cardGridsToRight < column_span
        ? position + (column_span - cardGridsToRight)
        : position

      this.bannersData[columnCount] = {
        ...bannerData,
        [adjustedPosition]: this.formatBannerData(banner, columnCount),
      }
    },

    /**
     * Format Banner Data.
     * @param {Object} banner - Banner data.
     * @param {Number} columnCount - Grid structure column count (2, 3 or 4).
     * @returns {Object} - Returns formatted data.
     */
    formatBannerData(banner, columnCount) {
      // eslint-disable-next-line prefer-const
      let { column_span = 1, row_span = 1, position = -1 } = this.getBannerDataForLayout(banner, columnCount)
      column_span = this.adjustColumnSpan(banner, columnCount)

      return {
        ...banner,
        [`${columnCount}-grid-layout`]: {
          column_span,
          position,
          row_span,
        },
        "image": {
          "alt":null,
          "src": banner.image,
        },
        "imageDesktop": {
          "alt":null,
          "src": banner.imageDesktop,
        },
      }
    },

    /**
     * Custom data event handler.
     * Populates bannersData.
     * @param {Array} data - Custom data.
     */
    populateCustomData(data) {
      if(!data?.length) {
        this.bannersData = {}
        return
      }

      data.forEach(banner => {
        this.mapBannerToPosition(banner, 4)
        this.mapBannerToPosition(banner, 3)
        this.mapBannerToPosition(banner, 2)
      })
    },

    /**
     * Returns banner data for specific layout.
     * @param {Object} banner - Banner data.
     * @param {Number} columnCount - Grid structure column count (2, 3 or 4).
     * @returns {Object} - Returns banner data for specific layout.
     */
    getBannerDataForLayout(banner, columnCount) {
      const bannerData = banner[`${columnCount}-grid-layout`]
      return bannerData || {}
    },

    /**
     * Get the banner content for a given position.
     * - If the position is the final product, then do not return in grid
     *   content so that we don't have in grid content hanging on its own.
     * @param {Number} position - Position where to render banner.
     * @returns {Object} - Return banner content.
     */
    getBannerContent(position) {
      if (position === this.productsCount) {
        return null
      }

      return this.bannersDataForLayout?.[position]
    },

    /**
     * Set number of hits per algolia index.
     * @param {Number} nbHits - number of hits/results.
     */
    nbHitsChange(nbHits) {
      this.productsCount = nbHits
    },

    updateRetrievedHits(items = []) {
      this.retrievedHitsCount = items.length
    },

    /**
     * page-change event handler.
     */
    pageChange() {
      if(!this.$refs.mainCollection) {
        return
      }

      this.$refs.mainCollection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    },

    /**
     * filters-applied event handler.
     * @param {Boolean} isFiltered - Is collection filtered.
     */
    filtersChange(isFiltered) {
      this.isFiltered = isFiltered
    },

    /**
     * Get dynamic product count message.
     * @param {Number} loadedProductCount - loaded products count.
     * @param {Number} totalCount - total products count.
     * @returns {String}
     */
    productCountMessage(loadedProductCount, totalCount) {
      return this.$string('general.pagination.showing_product_count',
        {
          replace: {
            products: loadedProductCount,
            total: totalCount,
          },
        })
    },

    /**
     * Generates a badge object from the given product.
     * @param {Object} - Product object.
     * @returns {Object}
     */
    generateProductBadge(product) {
      return generateProductBadge(product)
    },

    /**
     * Open mobile drawer on filter button click.
     */
    handleOpenMobileDrawer() {
      this.openOverlay({
        component: 'algolia-drawer-mobile',
        ignoreDismissed: true,
        namespace: 'algoliaDrawerMobile',
      })
    },

    /**
     * Handle hidden sale logic.
     * - Sets cookie if hidden sale query param is present.
     */
    handleHiddenSale() {
      if (
        this.$variable('page.templateSuffix') !== 'sale' ||
        !location.search.includes('hidden-sale=true')
      ) {
        return
      }

      const date = new Date()
      date.setTime(date.getTime() + (1 * 24 * 60 * 60 * 1000))
      const expires = `expires=${date.toUTCString()}`
      document.cookie = `ls-can-view-hidden-sale=true;${expires};path=/`
    },

    /**
     * page-size-update event handler.
     * @param size - number - page size.
     */
    updatePageSize(size) {
      this.perPage = size
    },
  },
}
</script>

<style lang="scss">
@import './main-collection';
@import './algolia-filter';
@import './algolia-facet';
@import './algolia-filters-slider';
@import './algolia-active-value-tag';
@import '../algolia-pagination/algolia-pagination';
@import '../related-links/related-links';
</style>
